import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { addMinutes, format, parseISO } from 'date-fns';
import { Building2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { generateLocalBusinessSchema } from '@/utils/seo';
import { getDateLocale } from '@/utils/dateLocale';
import { cn } from '@/lib/utils';
import { FULL_PAGE, type ProfileDisplayOptions, type PublicOrganization } from '../booking-page/types';
import { useBookingFlow } from './hooks/useBookingFlow';
import { ServiceSelection } from './steps/ServiceSelection';
import { DateTimeSelection } from './steps/DateTimeSelection';
import { CustomerDetails, DETAILS_FORM_ID } from './steps/CustomerDetails';
import { BookingConfirmation } from './steps/BookingConfirmation';
import { StepProgress } from './frame/StepProgress';
import { ServiceChip, WhenChip } from './frame/SelectionChips';
import { SummaryRail, type SummaryLine } from './frame/SummaryRail';
import { MobileStepBar } from './frame/MobileStepBar';

// Steps 1–3 (time, details, done): the content below the business header.
// Top bar, photos, header and footer are BookingPageFrame (see book.tsx),
// which stays mounted when the visitor moves here from the profile page, so
// only this part changes.
// Step 0 (choose a service) is the profile page itself.
//
// display: inside an embed the sidebar follows the embed options
// (?sidebar=1).
export default function BookingWizard({ display = FULL_PAGE }: { display?: ProfileDisplayOptions }) {
  const flow = useBookingFlow();
  const { t } = useTranslation(['bookingPage', 'publicBooking']);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const {
    orgSlug,
    organization,
    currentStep,
    updateStep,
    navigateStep,
    isLoading,
    isLoadingServices,
    isLoadingSlots,
    isSavingBooking,
    services,
    selectedService,
    handleServiceSelect,
    providers,
    selectedProvider,
    handleProviderSelect,
    selectedDate,
    setSelectedDate,
    currentWeekStart,
    setCurrentWeekStart,
    datesWithSlots,
    hasFutureSlots,
    availableSlots,
    selectedSlot,
    handleSlotSelect,
    customerForm,
    handleCustomerSubmit,
    customAnswers,
    setCustomAnswers,
    bookingConfirmation,
    generateGoogleCalendarUrl,
    clearBookingStorage,
    formatCurrency,
  } = flow;

  // Inline, with some height, so the frame above keeps its place while the
  // steps load their data.
  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center" data-testid="booking-steps-loading">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Building2 className="mx-auto mb-4 h-12 w-12 text-soft" />
            <h2 className="mb-2 text-h2 text-ink">{t('publicBooking:errors.organizationNotFound')}</h2>
            <p className="text-soft">{t('publicBooking:errors.organizationNotFoundMessage')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The public endpoint returns the booking page block next to the fields the
  // wizard has always used.
  const publicOrg = organization as unknown as PublicOrganization;
  const page = publicOrg.booking_page;

  const orgName = organization.name || organization.business_name;
  const bookingUrl = `https://slotyapp.com/book/${orgSlug}`;
  const seoTitle = t('publicBooking:wizard.seo.title', { orgName, defaultValue: `${orgName} — Book Online | Sloty` });
  const orgDescription =
    organization.description ||
    t('publicBooking:wizard.seo.description', { orgName, defaultValue: `Book an appointment with ${orgName}. Easy online scheduling.` });
  const localBusinessSchema = generateLocalBusinessSchema({
    businessType: organization.business_type || 'LocalBusiness',
    name: orgName,
    description: orgDescription,
  });

  const locale = getDateLocale();
  const duration = selectedService ? selectedService.duration_minutes ?? selectedService.duration : undefined;
  const price =
    selectedService?.price != null && !selectedService.hide_price ? formatCurrency(selectedService.price) : undefined;
  const isGroup = (selectedService?.max_capacity ?? 1) > 1;
  const isWaitlist = !!(selectedSlot?.isFull && selectedSlot?.waitlistEnabled);
  const personName = selectedSlot?.providerName || selectedProvider?.displayName;

  const slotStart = selectedSlot ? parseISO(selectedSlot.startTime) : null;
  const slotShort = slotStart ? format(slotStart, 'EEE d MMM · HH:mm', { locale }) : null;
  const slotRange = slotStart
    ? `${format(slotStart, 'EEE d MMM · HH:mm', { locale })} – ${format(addMinutes(slotStart, duration || 60), 'HH:mm')}`
    : null;

  const serviceMeta = [duration ? t('steps.minutes', { count: duration }) : null, price].filter(Boolean).join(' · ');
  const summaryRows: SummaryLine[] = [
    { label: t('steps.summary.service'), value: selectedService ? [selectedService.name, duration ? t('steps.minutes', { count: duration }) : null].filter(Boolean).join(' · ') : null },
    { label: t('steps.with'), value: personName || t('steps.anyAvailable') },
    { label: t('steps.summary.when'), value: slotShort },
  ];

  const backToPage = () => updateStep(0);
  const toTimes = () => updateStep(1);

  // Each step gets its own button element (key): if step 1's Continue were
  // reused as step 2's submit button, the same click would submit the form.
  const primaryButton = (size: 'rail' | 'bar') => {
    const cls = size === 'rail' ? 'h-[52px] w-full rounded-lg text-[15px] font-semibold' : 'h-14 shrink-0 rounded-lg px-6 text-[16px] font-semibold';
    if (currentStep === 1) {
      // Before a time is picked the button is still the only action on the
      // screen, so it has to read as a button. The default disabled style
      // (opacity 50%) turns it into a pale blue smudge that phone visitors
      // scan straight past; give it a solid inactive treatment instead.
      return (
        <Button
          key="continue"
          type="button"
          className={cn(cls, !selectedSlot && 'border border-line bg-surface-2 text-soft disabled:opacity-100')}
          disabled={!selectedSlot}
          onClick={() => navigateStep('next')}
          data-testid="step-continue"
        >
          {t('steps.continue')}
        </Button>
      );
    }
    return (
      <Button key="confirm" type="submit" form={DETAILS_FORM_ID} className={cls} disabled={isSavingBooking} data-testid="step-confirm">
        {isSavingBooking && <Loader2 className="h-4 w-4 animate-spin" />}
        {isWaitlist ? t('publicBooking:navigation.joinWaitlist', 'Join waitlist') : t('steps.confirmBooking')}
      </Button>
    );
  };

  const withSidebar = display.sidebar && (currentStep === 1 || currentStep === 2);
  const withBar = currentStep === 1 || currentStep === 2;

  const heading = (text: string) => (
    <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-ink sm:text-[32px]">{text}</h1>
  );

  const serviceChip = selectedService && (
    <ServiceChip name={selectedService.name} meta={serviceMeta} group={isGroup} onChange={backToPage} />
  );

  let main: React.ReactNode;
  if (currentStep === 1) {
    main = (
      <>
        <StepProgress active={0} onBack={backToPage} />
        {heading(t('steps.pickTime'))}
        {serviceChip}
        <div className="flex flex-col gap-6 sm:rounded-[18px] sm:border sm:border-line sm:bg-surface sm:p-6">
          <DateTimeSelection
            organization={publicOrg}
            selectedService={selectedService}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            currentWeekStart={currentWeekStart}
            setCurrentWeekStart={setCurrentWeekStart}
            datesWithSlots={datesWithSlots}
            hasFutureSlots={hasFutureSlots}
            availableSlots={availableSlots}
            selectedSlot={selectedSlot}
            onSelectSlot={handleSlotSelect}
            isLoadingSlots={isLoadingSlots}
            providers={providers}
            selectedProvider={selectedProvider}
            onSelectProvider={handleProviderSelect}
          />
        </div>
        {!withSidebar && <div className="hidden justify-end lg:flex">{primaryButton('bar')}</div>}
      </>
    );
  } else if (currentStep === 2) {
    main = (
      <>
        <StepProgress active={1} onBack={backToPage} />
        {heading(isWaitlist ? t('steps.joinWaitlist') : t('steps.yourDetails'))}
        <div className="hidden sm:block">{serviceChip}</div>
        {slotShort && (
          <WhenChip
            title={slotShort}
            detail={[selectedService?.name, personName].filter(Boolean).join(' · ')}
            onChange={toTimes}
            tone={isWaitlist ? 'plain' : 'brand'}
          />
        )}
        <CustomerDetails
          selectedService={selectedService}
          selectedSlot={selectedSlot}
          organization={organization}
          customerForm={customerForm}
          onSubmit={handleCustomerSubmit}
          customAnswers={customAnswers}
          setCustomAnswers={setCustomAnswers}
          isSavingBooking={isSavingBooking}
          onBack={toTimes}
          showActions={!withSidebar}
        />
      </>
    );
  } else if (currentStep >= 3 && bookingConfirmation) {
    main = (
      <>
        <StepProgress active={2} />
        <BookingConfirmation
          bookingConfirmation={bookingConfirmation}
          organization={publicOrg}
          selectedService={selectedService}
          selectedSlot={selectedSlot}
          whenLabel={slotRange}
          priceLabel={price}
          personName={personName}
          generateGoogleCalendarUrl={generateGoogleCalendarUrl}
          clearBookingStorage={clearBookingStorage}
        />
      </>
    );
  } else {
    // Step 0 is normally the profile page (book.tsx); this is a fallback,
    // e.g. step=3 after a reload, when the booking is no longer in memory.
    main = (
      <>
        {heading(t('steps.chooseService'))}
        <ServiceSelection
          services={services}
          selectedService={selectedService}
          onSelectService={(s) => {
            handleServiceSelect(s);
            updateStep(1);
          }}
          isLoadingServices={isLoadingServices}
          formatCurrency={formatCurrency}
        />
      </>
    );
  }

  return (
    <div style={{ touchAction: 'pan-y' }} data-testid="booking-steps" data-step={currentStep}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={orgDescription} />
        <link rel="canonical" href={bookingUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={bookingUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={orgDescription} />
        <meta property="og:image" content={organization.logo_url || 'https://slotyapp.com/og-image.jpg'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={orgDescription} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <div className={cn('grid items-start gap-10', withSidebar && 'lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-12')}>
        <div className={cn('flex min-w-0 flex-col gap-5 sm:gap-6', currentStep >= 3 && 'mx-auto w-full max-w-[1040px]')}>{main}</div>
        {withSidebar && (
          <SummaryRail rows={summaryRows} total={price} cta={primaryButton('rail')} cancellationHours={page?.cancellation_hours} />
        )}
      </div>

      {withBar && (
        <MobileStepBar
          embedded={display.embedded}
          line1={slotShort || t('steps.pickTime')}
          line2={
            <>
              {isWaitlist ? t('steps.waitlist') : price}
              {!isWaitlist && (
                <>
                  {price && <span aria-hidden="true">·</span>}
                  <button type="button" onClick={() => setSummaryOpen(true)} className="font-medium text-brand-500">
                    {t('steps.summary.title')}
                  </button>
                </>
              )}
            </>
          }
          action={primaryButton('bar')}
        />
      )}

      <Sheet open={summaryOpen} onOpenChange={setSummaryOpen}>
        <SheetContent side="bottom" className="rounded-t-[20px] pb-[max(24px,env(safe-area-inset-bottom))]" data-testid="summary-sheet">
          <SheetTitle className="mb-4 font-display text-[20px] font-semibold">{t('steps.summary.title')}</SheetTitle>
          <dl className="flex flex-col gap-3">
            {summaryRows.map((row) => (
              <div key={row.label} className="flex justify-between gap-4 text-[15px]">
                <dt className="text-soft">{row.label}</dt>
                <dd className="text-right font-medium">{row.value || '—'}</dd>
              </div>
            ))}
            {price && (
              <div className="flex items-baseline justify-between border-t border-line pt-3">
                <dt className="font-semibold">{t('steps.summary.total')}</dt>
                <dd className="font-display text-[22px] font-semibold tabular-nums">{price}</dd>
              </div>
            )}
          </dl>
          {page?.cancellation_hours != null && (
            <p className="mt-4 text-[13px] text-soft">{t('steps.freeCancellation', { count: page.cancellation_hours })}</p>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
